import { CustomerServicePanel } from '../../components/customer-services.js';
import { CustomerApplicationSettings } from '../../components/customer-application-settings.js';
import { ScheduledTasksPanel } from '../../components/scheduled-tasks-panel.js';
import { CustomerDatabasePanel } from '../../components/customer-databases.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EnvVarDto,
  PortalDeploymentDetailDto,
  PortalDeploymentsDto,
  PortalEnvVarsDto,
  PortalResourceDetailDto,
  ResourceConsoleCommandResultDto,
  ResourceConsoleOperation,
  ResourceConsoleSessionDto,
} from '@cpm/shared';
import { RESOURCE_CONSOLE_OPERATIONS } from '@cpm/shared';
import { ArrowLeft, ExternalLink, Play, RefreshCw, RotateCw, Terminal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api/client.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  Spinner,
  PageHeader,
  statusTone,
} from '../../components/ui.js';

function DeploymentLogs({ resourceId, deploymentUuid }: { resourceId: string; deploymentUuid: string }) {
  const detail = useQuery({
    queryKey: ['portal', 'resources', resourceId, 'deployments', deploymentUuid],
    queryFn: () =>
      api.get<PortalDeploymentDetailDto>(
        `/api/portal/resources/${resourceId}/deployments/${deploymentUuid}`,
      ),
  });

  if (detail.isPending) return <Spinner label="Loading build log…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const logs = detail.data.logs;
  return (
    <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs leading-relaxed text-slate-200">
      {logs.length === 0
        ? 'No log output.'
        : logs.map((line) => `${line.timestamp ? `[${line.timestamp}] ` : ''}${line.output}\n`).join('')}
    </pre>
  );
}

/** Deploy / restart — the plan's `deployments` feature. */
function ActionsCard({ resourceId }: { resourceId: string }) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['portal', 'resources', resourceId] });
  };
  const deploy = useMutation({
    mutationFn: () => api.post(`/api/portal/resources/${resourceId}/deploy`),
    onSuccess: invalidate,
  });
  const restart = useMutation({
    mutationFn: () => api.post(`/api/portal/resources/${resourceId}/restart`),
    onSuccess: invalidate,
  });
  return (
    <Card>
      <CardHeader title="Actions" description="Trigger a fresh deployment or restart this application." />
      <div className="flex flex-wrap items-center gap-2 px-5 pb-4">
        <Button loading={deploy.isPending} onClick={() => deploy.mutate()}>
          <Play className="size-4" /> Deploy
        </Button>
        <Button variant="secondary" loading={restart.isPending} onClick={() => restart.mutate()}>
          <RotateCw className="size-4" /> Restart
        </Button>
      </div>
      {(deploy.error != null || restart.error != null) && (
        <div className="px-5 pb-4">
          <ErrorNote error={deploy.error ?? restart.error} />
        </div>
      )}
    </Card>
  );
}

/** Poll-based runtime logs — the plan's `logs` feature. */
function LogsCard({ resourceId }: { resourceId: string }) {
  const logs = useQuery({
    queryKey: ['portal', 'resources', resourceId, 'logs'],
    queryFn: () => api.get<{ logs: string }>(`/api/portal/resources/${resourceId}/logs?lines=200`),
  });
  return (
    <Card>
      <CardHeader
        title="Runtime logs"
        description="The most recent output from the running container."
        actions={
          <Button variant="secondary" loading={logs.isFetching} onClick={() => logs.refetch()}>
            <RefreshCw className="size-4" /> Refresh
          </Button>
        }
      />
      <div className="px-5 pb-4">
        {logs.isPending ? (
          <Spinner />
        ) : logs.error ? (
          <ErrorNote error={logs.error} />
        ) : (
          <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs leading-relaxed text-slate-200">
            {logs.data.logs.trim().length === 0 ? 'No output.' : logs.data.logs}
          </pre>
        )}
      </div>
    </Card>
  );
}

function ConsoleCard({ resourceId }: { resourceId: string }) {
  const [session, setSession] = useState<ResourceConsoleSessionDto | null>(null);
  const [operation, setOperation] =
    useState<ResourceConsoleOperation>('working-directory');
  const [path, setPath] = useState('.');
  const [result, setResult] = useState<ResourceConsoleCommandResultDto | null>(null);
  const start = useMutation({
    mutationFn: () =>
      api.post<ResourceConsoleSessionDto>(
        `/api/portal/resources/${resourceId}/console/sessions`,
        { durationMinutes: 15 },
      ),
    onSuccess: setSession,
  });
  const run = useMutation({
    mutationFn: () =>
      api.post<ResourceConsoleCommandResultDto>(
        `/api/portal/console/sessions/${session!.id}/commands`,
        {
          operation,
          ...(operation === 'list-files' || operation === 'disk-usage' ? { path } : {}),
        },
      ),
    onSuccess: setResult,
  });
  const stop = useMutation({
    mutationFn: () => api.delete(`/api/portal/console/sessions/${session!.id}`),
    onSuccess: () => setSession(null),
  });
  return (
    <Card>
      <CardHeader
        title="Resource console"
        description="Audited, short-lived operations inside this container. This is not a host shell."
      />
      <div className="space-y-3 px-5 pb-4">
        {!session ? (
          <Button loading={start.isPending} onClick={() => start.mutate()}>
            <Terminal className="size-4" /> Start 15-minute session
          </Button>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone="green">active</Badge>
              <span className="text-xs text-slate-500">
                expires {new Date(session.expiresAt).toLocaleTimeString()} · {session.commandCount}/
                {session.maxCommands} commands
              </span>
              <Button
                className="ml-auto"
                variant="secondary"
                loading={stop.isPending}
                onClick={() => stop.mutate()}
              >
                Stop
              </Button>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="mb-1 block text-xs text-slate-500">Operation</span>
                <select
                  className="rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                  value={operation}
                  onChange={(event) => setOperation(event.target.value as ResourceConsoleOperation)}
                >
                  {RESOURCE_CONSOLE_OPERATIONS.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              {(operation === 'list-files' || operation === 'disk-usage') && (
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">Relative path</span>
                  <Input value={path} onChange={(event) => setPath(event.target.value)} />
                </label>
              )}
              <Button loading={run.isPending} onClick={() => run.mutate()}>Run</Button>
            </div>
            {result && (
              <pre className="max-h-72 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-200">
                {result.output || `(exit ${result.exitCode}, no output)`}
              </pre>
            )}
          </>
        )}
        {(start.error ?? run.error ?? stop.error) && (
          <ErrorNote error={start.error ?? run.error ?? stop.error} />
        )}
      </div>
    </Card>
  );
}

/** Environment variables — the plan's `environment_variables` feature. */
function EnvCard({ resourceId }: { resourceId: string }) {
  const queryClient = useQueryClient();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [shownOnce, setShownOnce] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const envs = useQuery({
    queryKey: ['portal', 'resources', resourceId, 'envs'],
    queryFn: () => api.get<PortalEnvVarsDto>(`/api/portal/resources/${resourceId}/envs`),
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['portal', 'resources', resourceId, 'envs'] });

  const create = useMutation({
    mutationFn: (body: { key: string; value: string; isShownOnce: boolean }) =>
      api.post(`/api/portal/resources/${resourceId}/envs`, body),
    onSuccess: () => {
      setNewKey('');
      setNewValue('');
      setShownOnce(false);
      void invalidate();
    },
  });
  const update = useMutation({
    mutationFn: (body: { key: string; value: string }) =>
      api.patch(`/api/portal/resources/${resourceId}/envs`, body),
    onSuccess: () => void invalidate(),
  });
  const remove = useMutation({
    mutationFn: (uuid: string) => api.delete(`/api/portal/resources/${resourceId}/envs/${uuid}`),
    onSuccess: () => void invalidate(),
  });

  if (envs.isPending) return <Spinner />;
  if (envs.error) return <ErrorNote error={envs.error} />;
  if (!envs.data.featureEnabled) return null;

  const vars: EnvVarDto[] = envs.data.vars;
  const mutationError = create.error ?? update.error ?? remove.error;

  return (
    <Card>
      <CardHeader
        title="Environment variables"
        description="Changes take effect on the next deployment."
      />
      {envs.data.error ? (
        <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">{envs.data.error}</p>
      ) : (
        <div className="space-y-4 px-5 pb-5">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {vars.map((v) => (
                  <tr key={v.uuid}>
                    <td className="py-2 pr-3 font-mono text-xs align-middle">{v.key}</td>
                    <td className="py-2 pr-3">
                      {v.isShownOnce ? (
                        <span className="text-xs text-slate-400">•••••• (write-only)</span>
                      ) : (
                        <Input
                          value={edits[v.uuid] ?? v.value ?? ''}
                          onChange={(e) => setEdits((s) => ({ ...s, [v.uuid]: e.target.value }))}
                        />
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!v.isShownOnce && edits[v.uuid] !== undefined && edits[v.uuid] !== v.value && (
                          <Button
                            variant="secondary"
                            className="px-2 py-1 text-xs"
                            loading={update.isPending}
                            onClick={() => update.mutate({ key: v.key, value: edits[v.uuid] ?? '' })}
                          >
                            Save
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          className="px-2 py-1"
                          loading={remove.isPending}
                          onClick={() => remove.mutate(v.uuid)}
                          aria-label={`Delete ${v.key}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {vars.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-3 text-sm text-slate-500 dark:text-slate-400">
                      No variables yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-800/40 sm:grid-cols-[1fr_1fr_auto]">
            <div>
              <Label htmlFor="env-key">Key</Label>
              <Input
                id="env-key"
                value={newKey}
                placeholder="MY_VAR"
                onChange={(e) => setNewKey(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="env-value">Value</Label>
              <Input
                id="env-value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                loading={create.isPending}
                disabled={!newKey.trim()}
                onClick={() => create.mutate({ key: newKey, value: newValue, isShownOnce: shownOnce })}
              >
                Add
              </Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 sm:col-span-3">
              <input
                type="checkbox"
                checked={shownOnce}
                onChange={(e) => setShownOnce(e.target.checked)}
              />
              Write-only (value hidden after saving — for secrets)
            </label>
          </div>
          {mutationError != null && <ErrorNote error={mutationError} />}
        </div>
      )}
    </Card>
  );
}

function DeploymentsCard({ resourceId }: { resourceId: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const deployments = useQuery({
    queryKey: ['portal', 'resources', resourceId, 'deployments'],
    queryFn: () => api.get<PortalDeploymentsDto>(`/api/portal/resources/${resourceId}/deployments`),
  });

  if (deployments.isPending) return <Spinner />;
  if (deployments.error) return <ErrorNote error={deployments.error} />;
  const data = deployments.data;
  if (!data.featureEnabled) return null;

  return (
    <Card>
      <CardHeader
        title="Deployments"
        description="Build history — select a deployment to read its build log"
      />
      {data.error ? (
        <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">{data.error}</p>
      ) : data.deployments.length === 0 ? (
        <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">No deployments yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Commit</th>
                <th className="px-5 py-3 font-medium">Started</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.deployments.map((deployment) => (
                <tr
                  key={deployment.deploymentUuid}
                  className={
                    'cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50' +
                    (selected === deployment.deploymentUuid ? ' bg-slate-50 dark:bg-slate-800/50' : '')
                  }
                  onClick={() =>
                    setSelected(selected === deployment.deploymentUuid ? null : deployment.deploymentUuid)
                  }
                >
                  <td className="px-5 py-3">
                    <Badge tone={statusTone(deployment.status)}>{deployment.status}</Badge>
                  </td>
                  <td className="px-5 py-3">
                    <p className="font-mono text-xs">{deployment.commit?.slice(0, 8) ?? '—'}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {deployment.commitMessage ?? ''}
                    </p>
                  </td>
                  <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                    {deployment.createdAt ? new Date(deployment.createdAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {selected && (
            <div className="border-t border-slate-100 p-5 dark:border-slate-800">
              <DeploymentLogs resourceId={resourceId} deploymentUuid={selected} />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function PortalResourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detail = useQuery({
    queryKey: ['portal', 'resources', id],
    queryFn: () => api.get<PortalResourceDetailDto>(`/api/portal/resources/${id}`),
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });

  if (detail.isPending) return <Spinner label="Loading resource…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const {
    resource,
    subscription,
    live,
    liveError,
    deploymentsEnabled,
    logsEnabled,
    envEnabled,
    consoleEnabled,
  } = detail.data;
  const status = live?.status ?? resource.status;

  return (
    <div>
      <Link
        to={`/portal/subscriptions/${subscription.id}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600 dark:text-slate-400"
      >
        <ArrowLeft className="size-4" /> {subscription.name}
      </Link>
      <PageHeader
        title={resource.name}
        description={resource.kind}
        actions={<Badge tone={statusTone(status)}>{status}</Badge>}
      />

      {detail.data.gitManagementEnabled && <Link className="mb-4 inline-block text-brand-600 underline" to={`/portal/git/sites/${resource.id}`}>Git and build settings</Link>}
      <div className="space-y-6">
        <Card>
          <CardHeader title="Details" />
          <div className="grid grid-cols-1 gap-3 px-5 pb-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-slate-500 dark:text-slate-400">Status</p>
              <p className="font-medium">{status}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Domain</p>
              {live?.fqdn ? (
                <a
                  href={live.fqdn}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline"
                >
                  {live.fqdn.replace(/^https?:\/\//, '')} <ExternalLink className="size-3.5" />
                </a>
              ) : (
                <p className="font-medium">—</p>
              )}
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Image / stack</p>
              <p className="font-medium">{live?.image ?? '—'}</p>
            </div>
          </div>
          {liveError && (
            <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">{liveError}</p>
          )}
        </Card>

        {deploymentsEnabled && <ActionsCard resourceId={resource.id} />}
        {detail.data.applicationSettingsEnabled && <CustomerApplicationSettings resourceId={resource.id} />}
        {detail.data.databaseManagementEnabled && <CustomerDatabasePanel resourceId={resource.id} subscriptionId={subscription.id} />}
        {detail.data.serviceManagementEnabled && <CustomerServicePanel resourceId={resource.id} subscriptionId={subscription.id} />}
        {detail.data.scheduledTaskManagementEnabled && <ScheduledTasksPanel appId={resource.id} enabled customer />}
        {logsEnabled && <LogsCard resourceId={resource.id} />}
        {envEnabled && <EnvCard resourceId={resource.id} />}
        {consoleEnabled && <ConsoleCard resourceId={resource.id} />}
        {deploymentsEnabled && <DeploymentsCard resourceId={resource.id} />}
      </div>
    </div>
  );
}
